export const REDIRECT_QUERY_PARAM = 'redirect';

const internalRedirectOrigin = 'https://countercyclical.local';
const blockedRedirectPathPrefixes = ['/auth', '/login', '/register', '/magiclink'];
const planKeys = ['plus', 'pro', 'premium', 'enterprise'] as const;

type RouterLocationLike = {
    pathname: string;
    search?: string;
    hash?: string;
};

const isBlockedRedirectPath = (pathname: string): boolean =>
    blockedRedirectPathPrefixes.some((prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`));

const decodeRedirectCandidate = (value: string): string => {
    try {
        return decodeURIComponent(value);
    } catch {
        return value;
    }
};

export const sanitizeRedirect = (value?: string | null, fallback = ''): string => {
    const rawValue = value?.trim();

    if (!rawValue) {return fallback;}

    const candidate = rawValue === 'onboarding' ? '/onboarding' : decodeRedirectCandidate(rawValue);

    if (!candidate.startsWith('/') || candidate.startsWith('//')) {return fallback;}

    try {
        const url = new URL(candidate, internalRedirectOrigin);

        if (url.origin !== internalRedirectOrigin || isBlockedRedirectPath(url.pathname)) {
            return fallback;
        }

        return `${url.pathname}${url.search}${url.hash}`;
    } catch {
        return fallback;
    }
};

export const isSafeRedirect = (value?: string | null): boolean => sanitizeRedirect(value) !== '';

export const sanitizePlanKey = (value?: string | null): string => {
    const normalizedValue = value?.trim().toLowerCase();

    if (!normalizedValue) {return '';}

    return planKeys.find((planKey) => normalizedValue.includes(planKey)) ?? '';
};

export const getRedirectFromSearch = (search?: string): string => {
    const redirect = new URLSearchParams(search ?? '').get(REDIRECT_QUERY_PARAM);

    return sanitizeRedirect(redirect);
};

export const getInternalPathFromLocation = (
    location: RouterLocationLike,
    options?: {
        stripRedirect?: boolean;
        stripStripeStatus?: boolean;
    },
): string => {
    const searchParams = new URLSearchParams(location.search ?? '');

    if (options?.stripRedirect) {
        searchParams.delete(REDIRECT_QUERY_PARAM);
    }

    if (options?.stripStripeStatus) {
        searchParams.delete('success');
        searchParams.delete('canceled');
    }

    const search = searchParams.toString();

    return `${location.pathname}${search ? `?${search}` : ''}${location.hash ?? ''}`;
};

export const appendRedirectToPath = (path: string, redirect?: string | null): string => {
    const safeRedirect = sanitizeRedirect(redirect);

    if (!safeRedirect) {return path;}

    const url = new URL(path, internalRedirectOrigin);

    url.searchParams.set(REDIRECT_QUERY_PARAM, safeRedirect);

    return `${url.pathname}${url.search}${url.hash}`;
};

export const appendSearchParamsToPath = (
    path: string,
    params: Record<string, string | number | boolean | null | undefined>,
): string => {
    const url = new URL(path, internalRedirectOrigin);

    Object.entries(params).forEach(([key, value]) => {
        if (value === null || value === undefined || value === '') {
            url.searchParams.delete(key);

            return;
        }

        url.searchParams.set(key, String(value));
    });

    return `${url.pathname}${url.search}${url.hash}`;
};
